import React, { useCallback, useEffect, useMemo, useState } from "react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ArrowDown, ArrowUp, ArrowUpDown, ClipboardPlus, Download, Loader2 } from "lucide-react"

import { MetadataTableRow } from "../types"
import { formatCurrency } from "../utils"
import { useToast } from "@/hooks/use-toast"
import type { ChannelConfigDTO } from "@/types/channel-config"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import { INTEGRATIONS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"






export type SortKey =
  | "utm_source"
  | "utm_campaign"
  | "utm_medium"
  | "referrer_url"
  | "orders"
  | "revenue"
  | "average_order_value"
  | "discount_code"

export type MetadataSortConfig = { key: SortKey; direction: "asc" | "desc" }

interface MetadataDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  subtitle?: string
  badgeLabel?: string
  totals: { orders: number; revenue: number }
  currency: string | null
  isLoading: boolean
  rows: MetadataTableRow[]
  onExport: (rows?: MetadataTableRow[], sortConfig?: MetadataSortConfig | null) => void
  isExportDisabled?: boolean
  linkedOrderCount?: number
  integrationId: string
  originChannelSlug?: string | null
  onSourceLinked?: () => void
}

const ALPHANUMERIC_REGEX = /[\p{L}\p{N}]/u

const isMeaningfulValue = (value: string | null | undefined): boolean => {
  if (!value) return false
  const trimmed = value.trim()
  if (trimmed.length === 0) return false
  return ALPHANUMERIC_REGEX.test(trimmed)
}

const normalizeUtmValue = (value: string | null | undefined): string => {
  if (!value) return ""
  const trimmed = value.trim()
  if (trimmed.length === 0) return ""
  return ALPHANUMERIC_REGEX.test(trimmed) ? trimmed : ""
}

const buildSourceLabel = (row: MetadataTableRow): string => {
  const source = normalizeUtmValue(row.utm_source)
  if (!source) {
    return "New Source"
  }

  return source.charAt(0).toUpperCase() + source.slice(1)
}

export const MetadataDialog = ({
  open,
  onOpenChange,
  title,
  subtitle,
  badgeLabel,
  totals,
  currency,
  isLoading,
  rows,
  onExport,
  isExportDisabled = false,
  linkedOrderCount = 0,
  integrationId,
  originChannelSlug = null,
  onSourceLinked,
}: MetadataDialogProps) => {



  const tUi = useTranslations("Shopify.ShopifyAttributionDashboardDialogsMetadataDialog")
  const tCommonUi = useTranslations("Common")

  const { toast } = useToast()
  const { fetchWithAuth } = useAuth()
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkingRow, setLinkingRow] = useState<MetadataTableRow | null>(null)
  const [channels, setChannels] = useState<ChannelConfigDTO[]>([])
  const [channelsLoading, setChannelsLoading] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState("")
  const [newChannelName, setNewChannelName] = useState("")
  const [includeCampaign, setIncludeCampaign] = useState(false)
  const [includeMedium, setIncludeMedium] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sortConfig, setSortConfig] = useState<MetadataSortConfig | null>(null)
  const { can } = usePermissions()

  const loadChannels = useCallback(async () => {
    if (!integrationId) {
      return
    }

    setChannelsLoading(true)
    try {
      const response = await fetchWithAuth(`/api/integrations/shopify/channel-config?integration_id=${integrationId}`)
      let data: { channels?: ChannelConfigDTO[]; error?: string } | null = null
      try {
        data = await response.json()
      } catch (_parseError) {
        data = null
      }

      if (!response.ok) {
        const message = data?.error ?? `Failed to load channels (${response.status})`
        throw new Error(message)
      }

      const nextChannels = Array.isArray(data?.channels) ? (data!.channels as ChannelConfigDTO[]) : []
      setChannels(nextChannels)
    } catch (error) {
      console.error("[Metadata Dialog] Failed to load channels:", error)
      toast({
        variant: "destructive",
        title: tUi("failedToLoadChannels"),
        description: tUi("pleaseTryAgainInAFewMoments"),
      })
    } finally {
      setChannelsLoading(false)
    }
  }, [fetchWithAuth, integrationId, tUi, toast])

  const selectableChannels = useMemo(
    () => channels.filter((channel) => !channel.isFallback && !channel.isSpecial),
    [channels],
  )

  const sortedChannels = useMemo(
    () =>
      [...selectableChannels].sort(
        (a, b) => (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER),
      ),
    [selectableChannels],
  )

  const rawUtmSource = linkingRow?.utm_source ?? ""
  const rawUtmCampaign = linkingRow?.utm_campaign ?? ""
  const rawUtmMedium = linkingRow?.utm_medium ?? ""

  const displayUtmSource = rawUtmSource.trim()

  const normalizedUtmSource = normalizeUtmValue(rawUtmSource)
  const normalizedUtmCampaign = normalizeUtmValue(rawUtmCampaign)
  const normalizedUtmMedium = normalizeUtmValue(rawUtmMedium)

  const canUseCampaign = normalizedUtmCampaign.length > 0
  const canUseMedium = normalizedUtmMedium.length > 0

  useEffect(() => {
    if (!open) {
      setLinkDialogOpen(false)
      setLinkingRow(null)
      setSelectedChannel("")
      setNewChannelName("")
      setIncludeCampaign(false)
      setIncludeMedium(false)
      setIsSubmitting(false)
    }
  }, [open])

  useEffect(() => {
    if (!canUseCampaign && includeCampaign) {
      setIncludeCampaign(false)
    }
  }, [canUseCampaign, includeCampaign])

  useEffect(() => {
    if (!canUseMedium && includeMedium) {
      setIncludeMedium(false)
    }
  }, [canUseMedium, includeMedium])

  const handleSelectChannel = useCallback((value: string) => {
    setSelectedChannel(value)
    setNewChannelName("")
  }, [])

  const handleNewChannelNameChange = useCallback((value: string) => {
    setNewChannelName(value)
    if (value.trim().length > 0) {
      setSelectedChannel("")
    }
  }, [])

  const handleSort = useCallback((key: SortKey) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: "asc" }
      }
      if (current.direction === "asc") {
        return { key, direction: "desc" }
      }
      return null
    })
  }, [])

  const getAriaSort = (key: SortKey): "ascending" | "descending" | "none" => {
    if (!sortConfig || sortConfig.key !== key) {
      return "none"
    }
    return sortConfig.direction === "asc" ? "ascending" : "descending"
  }

  const renderSortIndicator = (key: SortKey) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
    }

    return sortConfig.direction === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5 text-foreground" aria-hidden="true" />
    )
  }

  const sortedRows = useMemo(() => {
    if (!sortConfig) {
      return rows
    }

    return [...rows].sort((a, b) => {
      const { key, direction } = sortConfig
      const modifier = direction === "asc" ? 1 : -1

      switch (key) {
        case "orders":
        case "revenue":
        case "average_order_value": {
          const valueA = a[key] ?? 0
          const valueB = b[key] ?? 0
          if (valueA === valueB) {
            return 0
          }
          return valueA > valueB ? modifier : -modifier
        }
        case "utm_source":
        case "utm_campaign":
        case "utm_medium":
        case "referrer_url": {
          const valueA = (a[key] || "").toLowerCase()
          const valueB = (b[key] || "").toLowerCase()
          return valueA.localeCompare(valueB) * modifier
        }
        case "discount_code": {
          const valueA = (a.discount_code || "").toLowerCase()
          const valueB = (b.discount_code || "").toLowerCase()
          return valueA.localeCompare(valueB) * modifier
        }
        default:
          return 0
      }
    })
  }, [rows, sortConfig])

  const handleLinkDialogOpenChange = useCallback(
    (dialogOpen: boolean) => {
      if (!dialogOpen) {
        setLinkDialogOpen(false)
        setLinkingRow(null)
        setSelectedChannel("")
        setNewChannelName("")
        setIncludeCampaign(false)
        setIncludeMedium(false)
        setIsSubmitting(false)
      } else if (linkingRow) {
        setLinkDialogOpen(true)
      }
    },
    [linkingRow],
  )

  const handleInitiateLink = useCallback(
    (row: MetadataTableRow) => {
      const normalizedSource = normalizeUtmValue(row.utm_source)
      if (!normalizedSource || !integrationId) {
        return
      }

      setLinkingRow(row)
      setSelectedChannel("")
      const defaultChannelName = buildSourceLabel(row)
      setNewChannelName(defaultChannelName === "New Source" ? "" : defaultChannelName)
      setIncludeCampaign(false)
      setIncludeMedium(false)
      setIsSubmitting(false)
      setLinkDialogOpen(true)
      void loadChannels()
    },
    [integrationId, loadChannels],
  )

  const handleSubmit = useCallback(async () => {
    if (!linkingRow || !integrationId) {
      return
    }

    const trimmedSource = normalizedUtmSource
    const trimmedChannelName = newChannelName.trim()
    const targetChannelSlug = selectedChannel.trim()

    if (!trimmedSource || (!targetChannelSlug && !trimmedChannelName)) {
      toast({
        variant: "destructive",
        title: tUi("pickAChannel"),
        description: tUi("selectAnExistingChannelOrProvideAName"),
      })
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetchWithAuth(`/api/integrations/shopify/channel-config?integration_id=${integrationId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          utmSource: trimmedSource,
          utmMedium: normalizedUtmMedium,
          utmCampaign: normalizedUtmCampaign,
          includeMedium: includeMedium && canUseMedium,
          includeCampaign: includeCampaign && canUseCampaign,
          channelSlug: targetChannelSlug || undefined,
          channelName: targetChannelSlug ? undefined : trimmedChannelName || undefined,
          originChannelSlug: originChannelSlug || undefined,
          sourceLabel: buildSourceLabel(linkingRow),
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const message = data?.error ?? `Failed to create source (${response.status})`
        throw new Error(message)
      }

      setChannels(Array.isArray(data?.channels) ? data.channels : [])
      toast({
        title: tUi("sourceCreated"),
        description: tUi("uTMParametersWereLinkedToTheSelectedChannel"),
      })
      setLinkDialogOpen(false)
      setLinkingRow(null)
      setSelectedChannel("")
      setNewChannelName("")
      setIncludeCampaign(false)
      setIncludeMedium(false)
      onSourceLinked?.()
    } catch (error) {
      console.error("[Metadata Dialog] Failed to create channel source:", error)
      toast({
        variant: "destructive",
        title: tUi("couldNotCreateSource"),
        description: error instanceof Error ? error.message : "Please try again in a few moments.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [linkingRow, integrationId, normalizedUtmSource, newChannelName, selectedChannel, toast, tUi, fetchWithAuth, normalizedUtmMedium, normalizedUtmCampaign, includeMedium, canUseMedium, includeCampaign, canUseCampaign, originChannelSlug, onSourceLinked])

  const canSubmit = useMemo(() => {
    const sourceReady = normalizedUtmSource.length > 0
    const targetReady = selectedChannel.trim().length > 0 || newChannelName.trim().length > 0
    return sourceReady && targetReady && !isSubmitting
  }, [normalizedUtmSource, selectedChannel, newChannelName, isSubmitting])

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-4 mr-10">
              <DialogTitle className="flex-1">{title}</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExport([...sortedRows], sortConfig)}
                disabled={isLoading || isExportDisabled}
              >
                <Download className="mr-2 h-4 w-4" />
                <span><span key={tUi("exportToExcel")}>{tUi("exportToExcel")}</span></span>
              </Button>
            </div>
            {subtitle ? <DialogDescription>{subtitle}</DialogDescription> : null}
            {badgeLabel ? (
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground"><TranslationText>{tUi.rich("value0Details", { value0: String(badgeLabel) })}</TranslationText></span>
            ) : null}
          </DialogHeader>
          <div className="space-y-4 overflow-hidden">
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">{totals.orders}</span> <span><span key={tUi("orders")}>{tUi("orders")}</span></span>
              </div>
              <div>
                <span className="font-medium text-foreground">{formatCurrency(totals.revenue, currency ?? "USD")}</span>{" "}
                <span><span key={tUi("revenue")}>{tUi("revenue")}</span></span>
              </div>
              {linkedOrderCount > 0 ? (
                <div>
                  <span className="font-medium text-foreground">{linkedOrderCount}</span> <span><span key={tUi("linkedOrders")}>{tUi("linkedOrders")}</span></span>
                </div>
              ) : null}
            </div>
            <div className="rounded-md border">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : sortedRows.length === 0 ? (
                <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                  <span key={tUi("noDataAvailableForThisSelection")}>{tUi("noDataAvailableForThisSelection")}</span></div>
              ) : (
                <div className="max-h-[50vh] overflow-auto pr-2">
                  <div className="min-w-[720px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12" />
                          <TableHead
                            aria-sort={getAriaSort("utm_source")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("utm_source")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("utm_source")
                              }
                            }}
                            className="cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-1"><TranslationText>{tUi.rich("utmSourceValue0", { value0: String(renderSortIndicator("utm_source")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("utm_campaign")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("utm_campaign")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("utm_campaign")
                              }
                            }}
                            className="cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-1"><TranslationText>{tUi.rich("utmCampaignValue0", { value0: String(renderSortIndicator("utm_campaign")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("utm_medium")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("utm_medium")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("utm_medium")
                              }
                            }}
                            className="cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-1"><TranslationText>{tUi.rich("utmMediumValue0", { value0: String(renderSortIndicator("utm_medium")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("referrer_url")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("referrer_url")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("referrer_url")
                              }
                            }}
                            className="cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-1"><TranslationText>{tUi.rich("referrerUrlValue0", { value0: String(renderSortIndicator("referrer_url")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("orders")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("orders")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("orders")
                              }
                            }}
                            className="cursor-pointer select-none text-right"
                          >
                            <span className="flex w-full items-center justify-end gap-1"><TranslationText>{tUi.rich("ordersValue0", { value0: String(renderSortIndicator("orders")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("revenue")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("revenue")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("revenue")
                              }
                            }}
                            className="cursor-pointer select-none text-right"
                          >
                            <span className="flex w-full items-center justify-end gap-1"><TranslationText>{tUi.rich("revenueValue0", { value0: String(renderSortIndicator("revenue")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("average_order_value")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("average_order_value")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("average_order_value")
                              }
                            }}
                            className="cursor-pointer select-none text-right"
                          >
                            <span className="flex w-full items-center justify-end gap-1"><TranslationText>{tUi.rich("avgOrderValueValue0", { value0: String(renderSortIndicator("average_order_value")) })}</TranslationText></span>
                          </TableHead>
                          <TableHead
                            aria-sort={getAriaSort("discount_code")}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSort("discount_code")}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault()
                                handleSort("discount_code")
                              }
                            }}
                            className="cursor-pointer select-none"
                          >
                            <span className="flex items-center gap-1"><TranslationText>{tUi.rich("discountCodeValue0", { value0: String(renderSortIndicator("discount_code")) })}</TranslationText></span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedRows.map((row, index) => (
                          <TableRow
                            key={`${row.utm_source}-${row.utm_campaign}-${row.utm_medium}-${row.discount_code ?? "none"}-${index}`}
                          >
                            <TableCell className="w-12">
                              { can([INTEGRATIONS_PERMISSIONS.SHOPIFY_MANAGE]) && isMeaningfulValue(row.utm_source) ? (
                                <Button
                                  aria-label={tUi("linkUTMSourceToAChannel")}
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleInitiateLink(row)}
                                  disabled={isSubmitting || channelsLoading || !integrationId}
                                >
                                  <ClipboardPlus className="h-4 w-4" />
                                </Button>
                              ) : null}
                            </TableCell>
                            <TableCell>
                              <span className="flex items-center gap-1.5">
                                {row.utm_source || "—"}
                                {Boolean(row.aliased_orders) && (
                                  <span
                                    className="cursor-help text-xs text-muted-foreground"
                                    title={tUi("inValue0OfValue1OrdersAtLeastOne", { value0: String(row.aliased_orders), value1: String(row.orders) })}
                                  >
                                    <span key={tUi("aliased")}>{tUi("aliased")}</span></span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell>{row.utm_campaign || "—"}</TableCell>
                            <TableCell>{row.utm_medium || "—"}</TableCell>
                            <TableCell>{row.referrer_url || "—"}</TableCell>
                            <TableCell className="text-right">{row.orders}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(row.revenue, currency ?? "USD")}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(row.average_order_value, currency ?? "USD")}
                            </TableCell>
                            <TableCell>{row.discount_code ?? "—"}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialogOpen} onOpenChange={handleLinkDialogOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle><span><span key={tUi("createChannelSource")}>{tUi("createChannelSource")}</span></span> <i><span>[</span>{displayUtmSource}<span>]</span></i></DialogTitle>
          </DialogHeader>
          <div className="space-y-4">

            <div className="space-y-2">
              <Label htmlFor="metadata-channel-select"><span key={tUi("selectAChannel")}>{tUi("selectAChannel")}</span></Label>
              <Select value={selectedChannel} onValueChange={handleSelectChannel} disabled={channelsLoading}>
                <SelectTrigger id="metadata-channel-select">
                  <SelectValue placeholder={channelsLoading ? tCommonUi("loading") : tUi("existingChannel")} />
                </SelectTrigger>
                <SelectContent>
                  {channelsLoading ? (
                    <SelectItem value="__loading" disabled>
                      <span className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span key={tCommonUi("loading")}>{tCommonUi("loading")}</span></span>
                    </SelectItem>
                  ) : sortedChannels.length === 0 ? (
                    <SelectItem value="__empty" disabled>
                      <span className="text-muted-foreground"><span key={tUi("noChannelsFound")}>{tUi("noChannelsFound")}</span></span>
                    </SelectItem>
                  ) : (
                    sortedChannels.map((channel) => (
                      <SelectItem key={channel.slug} value={channel.slug}>
                        {channel.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="metadata-channel-name"><span key={tUi("orCreateANewChannel")}>{tUi("orCreateANewChannel")}</span></Label>
              <Input
                id="metadata-channel-name"
                placeholder={tUi("newChannelName")}
                value={newChannelName}
                onChange={(event) => handleNewChannelNameChange(event.target.value)}
              />
            </div>

            <div className="space-y-3">
              <Label><span key={tUi("includeInMatcher")}>{tUi("includeInMatcher")}</span></Label>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="metadata-utm-campaign"
                  checked={includeCampaign && canUseCampaign}
                  disabled={!canUseCampaign}
                  onCheckedChange={(value) => setIncludeCampaign(Boolean(value) && canUseCampaign)}
                />
                <Label
                  htmlFor="metadata-utm-campaign"
                  className={!canUseCampaign ? "text-muted-foreground" : undefined}
                >
                  <span key={"utm_campaign"}>{"utm_campaign"}</span></Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="metadata-utm-medium"
                  checked={includeMedium && canUseMedium}
                  disabled={!canUseMedium}
                  onCheckedChange={(value) => setIncludeMedium(Boolean(value) && canUseMedium)}
                />
                <Label
                  htmlFor="metadata-utm-medium"
                  className={!canUseMedium ? "text-muted-foreground" : undefined}
                >
                  <span key={"utm_medium"}>{"utm_medium"}</span></Label>
              </div>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleLinkDialogOpenChange(false)}
              disabled={isSubmitting}
            >
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              <span><span key={tUi("createSource")}>{tUi("createSource")}</span></span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

MetadataDialog.displayName = "MetadataDialog"
